import axiosClient from '../axiosClient';
import {
  CreateBoardRequest,
  UpdateBoardRequest,
  GetBoardsResponse,
  GetBoardResponse,
  CreateBoardResponse,
  UpdateBoardResponse,
  DeleteBoardResponse,
} from '../types/BoardTypes';


// Helper function to normalize board response (convert numeric IDs to strings)
const normalizeBoard = (board: any): any => ({
  ...board,
  id: String(board.id),
  ownerId: String(board.ownerId),
  workspaceId: board.workspaceId || '',
  members: board.members || [],
  color: board.color || '#0073ea',
  createdAt: board.createdAt || new Date().toISOString(),
  updatedAt: board.updatedAt || board.createdAt || new Date().toISOString(),
});

export const boardsApi = {
  // Get all boards for the current user
  getBoards: async (): Promise<GetBoardsResponse> => {
    try {
      const response = await axiosClient.get('/boards');
      // Backend returns array directly
      const boards = Array.isArray(response.data) ? response.data : response.data.boards || [];
      const normalizedBoards = boards.map(normalizeBoard);
      return { boards: normalizedBoards, total: normalizedBoards.length };
    } catch (error: any) {
      console.error('Network error during getBoards', error);
      throw error;
    }
  },

  // Get a specific board by ID
  getBoard: async (boardId: string): Promise<GetBoardResponse> => {
    try {
      const response = await axiosClient.get(`/boards/${boardId}`);
      const board = response.data.board || response.data;
      return { board: normalizeBoard(board) };
    } catch (error: any) {
      console.error(`Network error during getBoard ${boardId}`, error);
      throw error;
    }
  },

  // Create a new board
  createBoard: async (data: CreateBoardRequest): Promise<CreateBoardResponse> => {
    try {
      const response = await axiosClient.post('/boards', data);
      const board = response.data.board || response.data;
      return { board: normalizeBoard(board) };
    } catch (error: any) {
      console.error('Network error during createBoard', error);
      throw error;
    }
  },

  // Update an existing board
  updateBoard: async (
    boardId: string,
    data: UpdateBoardRequest
  ): Promise<UpdateBoardResponse> => {
    try {
      const response = await axiosClient.patch(`/boards/${boardId}`, data);
      const board = response.data.board || response.data;
      return { board: normalizeBoard(board) };
    } catch (error) {
      console.error('Failed to update board:', error);
      throw error;
    }
  },

  // Delete a board
  deleteBoard: async (boardId: string): Promise<DeleteBoardResponse> => {
    try {
      const response = await axiosClient.delete(`/boards/${boardId}`);
      return response.data || { success: true, message: 'Board deleted successfully' };
    } catch (error) {
      console.error('Failed to delete board:', error);
      throw error;
    }
  },

  // Add member to board
  addMember: async (
    boardId: string,
    userId: string,
    role?: 'ADMIN' | 'MEMBER' | 'VIEWER',
  ): Promise<any> => {
    const response = await axiosClient.post(`/boards/${boardId}/members`, {
      userId: Number(userId),
      role,
    });
    return response.data;
  },

  // Remove member from board
  removeMember: async (boardId: string, userId: string): Promise<any> => {
    const response = await axiosClient.delete(`/boards/${boardId}/members/${userId}`);
    return response.data;
  },

  // Update a member's role on the board
  updateMemberRole: async (
    boardId: string,
    userId: string,
    role: 'ADMIN' | 'MEMBER' | 'VIEWER',
  ): Promise<any> => {
    const response = await axiosClient.patch(`/boards/${boardId}/members/${userId}`, { role });
    return response.data;
  },

  // Get board members
  getBoardMembers: async (boardId: string): Promise<any[]> => {
    try {
      const response = await axiosClient.get(`/boards/${boardId}/members`);
      return Array.isArray(response.data) ? response.data : response.data.members || [];
    } catch (error) {
      console.error('Failed to fetch board members:', error);
      throw error;
    }
  },

  // Combined view: direct members + members inherited via an assigned team
  getBoardAccess: async (
    boardId: string,
  ): Promise<{ members: any[]; teams: { id: number; name: string; members: any[] }[] }> => {
    const response = await axiosClient.get(`/boards/${boardId}/access`);
    return response.data;
  },

  // Assign a team to a project
  assignTeam: async (boardId: string, teamId: number): Promise<any> => {
    const response = await axiosClient.post(`/boards/${boardId}/teams/${teamId}`);
    return response.data;
  },

  // Remove a team from a project (does not delete the team)
  unassignTeam: async (boardId: string, teamId: number): Promise<any> => {
    const response = await axiosClient.delete(`/boards/${boardId}/teams/${teamId}`);
    return response.data;
  },
};
